const template = document.createElement("template");

template.innerHTML = `
  <style>
    :host {
      display: inline-block;
      font-family: inherit;
      width: 100%;
    }

    .wrapper {
      display: flex;
      align-items: center;
      flex-direction: column;
      gap: 0.25rem;
    }

    button {
      align-items: center;
      background: transparent none;
      border: none;
      border-radius: 99px;
      box-shadow: none;
      color: var(--color-type);
      display: inline-flex;
      height: calc(0.875 * var(--margin));
      justify-content: center;
      letter-spacing: var(--letter-spacing);
      padding: 0;
      text-transform: uppercase;
      width: calc(0.875 * var(--margin));
      cursor: pointer;
    }

    button span {
      line-height: 1em;
      font-weight: 500;
      color: #6b6b6b;
      font-size: 17px;
      font-family: Tahoma;
    }
    
    .remove {
      line-height: 1.2em;
      opacity: .6;
      font-size: 80%;
    }

    input {
      color: black;
      font-size: 14px;
      width: 45px;
      text-align: center;
      background: #fbfbf6;
      border: none;
      padding: 14px 0;
      font-family: "jost", "helvetica neue", helvetica, arial, sans-serif;
      font-weight: 300;
    }

    .minus, .plus {
      width: 100%;
      height: auto;
      display: block;
    }

    .input-wrapper {
      display: flex;
      width: 100%;
      justify-content: center;
      border: 1px solid rgba(188, 188, 185, .75);
    }

    .error-message {
      color: red;
      font-size: 12px;
      margin-top: 5px;
    }

    .error-message.hidden {
      display: none;
    }
  </style>

  <div class="wrapper" part="wrapper">
    <div class="input-wrapper">
      <button part="minus" class="minus" aria-label="Decrease quantity"><span>−</span></button>
      <input part="input" class="qty" type="text" value="1" aria-label="Increase quantity">
      <button part="plus" class="plus" aria-label="Increase quantity"><span>+</span></button>
    </div>
    <button part="remove" class="remove">Remove</button>

    <div class="error-message hidden"></div>
  </div>
`;

class CartQuantityElement extends HTMLElement {
  private shadow: ShadowRoot;

  private line = 1;
  private qtyInput!: HTMLInputElement;
  private plusBtn!: HTMLButtonElement;
  private minusBtn!: HTMLButtonElement;
  private removeBtn!: HTMLButtonElement;
  private errorMessage!: HTMLDivElement;
  static get observedAttributes() {
    return ["line", "quantity"];
  }

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: "open" });
    this.shadow.appendChild(template.content.cloneNode(true));

    this.qtyInput = this.shadow.querySelector<HTMLInputElement>(".qty")!;
    this.plusBtn = this.shadow.querySelector<HTMLButtonElement>(".plus")!;
    this.minusBtn = this.shadow.querySelector<HTMLButtonElement>(".minus")!;
    this.removeBtn = this.shadow.querySelector<HTMLButtonElement>(".remove")!;
    this.errorMessage =
      this.shadow.querySelector<HTMLDivElement>(".error-message")!;
  }

  connectedCallback() {
    this.line = Number(this.getAttribute("line") ?? 1);
    this.qtyInput.value = this.getAttribute("quantity") ?? "1";

    this.plusBtn.addEventListener("click", () => this.changeBy(1));
    this.minusBtn.addEventListener("click", () => this.changeBy(-1));
    this.removeBtn.addEventListener("click", () => this.updateQuantity(0));

    this.qtyInput.addEventListener("change", () => {
      const value = Math.max(0, Number(this.qtyInput.value));
      this.updateQuantity(value);
    });
  }

  attributeChangedCallback(
    name: string,
    _old: string | null,
    value: string | null
  ) {
    if (name === "line") {
      this.line = Number(value ?? 1);
    }
    if (name === "quantity" && this.qtyInput) {
      this.qtyInput.value = value ?? "1";
    }
  }

  private changeBy(delta: number) {
    const current = Number(this.qtyInput.value);
    const newQty = Math.max(0, current + delta);
    this.updateQuantity(newQty);
  }

  private async updateQuantity(qty: number) {
    try {
      const res = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ line: this.line, quantity: qty }),
      });

      if (!res.ok) {
        const error = await res.json();
        this.errorMessage.textContent =
          error.description || "Error updating cart";
        this.errorMessage.classList.remove("hidden");
        return;
      }

      this.errorMessage.classList.add("hidden");

      const data = await res.json();

      if (qty === 0) {
        location.reload();
        return;
      }

      this.qtyInput.value = String(qty);

      this.dispatchEvent(
        new CustomEvent("cart-qty-change", {
          detail: { line: this.line, quantity: qty, cart: data },
          bubbles: true,
          composed: true,
        })
      );
    } catch (err) {
      console.error(err);
      this.errorMessage.textContent = "Error updating cart";
      this.errorMessage.classList.remove("hidden");
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "cart-quantity": CartQuantityElement;
  }
}

if (!window.customElements.get("cart-quantity")) {
  window.customElements.define("cart-quantity", CartQuantityElement);
}

export {};
